import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export const buttonStyles = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold transition-all select-none disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-sm hover:brightness-110 hover:shadow-glow',
        secondary: 'bg-secondary text-secondary-foreground hover:brightness-125',
        accent: 'bg-accent text-accent-foreground hover:brightness-105',
        outline: 'border border-input bg-card text-foreground hover:bg-muted',
        soft: 'bg-primary-soft text-primary hover:bg-primary/15',
        ghost: 'text-foreground hover:bg-muted',
        danger: 'bg-danger text-white hover:brightness-110',
        link: 'text-primary underline-offset-4 hover:underline px-0',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-5 text-sm',
        lg: 'h-12 px-7 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

type Style = VariantProps<typeof buttonStyles>;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, Style {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, type = 'button', ...rest }, ref) => (
    <button ref={ref} type={type} disabled={disabled || loading} className={cn(buttonStyles({ variant, size }), className)} {...rest}>
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

export function ButtonLink({ className, variant, size, ...rest }: LinkProps & Style) {
  return <Link className={cn(buttonStyles({ variant, size }), className)} {...rest} />;
}
